/**
 * شاخص اعتماد مشترک برای گزارش‌های داشبورد.
 * هر گزارش بر اساس منبع دادهٔ خودش (تعداد فاکتور، درصد اشتراک‌گیری یا تعداد ماه دادهٔ تاریخی)
 * یک سطح اعتماد ('good' | 'warning') به‌همراه برچسب کوتاه و جزئیات کامل (برای tooltip) می‌سازد.
 */

export type ConfidenceLevel = 'good' | 'warning';

export interface ConfidenceResult {
  level: ConfidenceLevel;
  label: string;
  detail: string;
}

/** حداقل مجموع فاکتورهای بررسی‌شده در بازه برای اعتماد کامل (نبض بازار). */
export const DEFAULT_INVOICE_COUNT_THRESHOLD = 500;
/** حداقل درصد اشتراک‌گیری برای اعتماد کامل (گزارش‌های مشتری‌محور). */
export const DEFAULT_SUBSCRIPTION_THRESHOLD_PERCENT = 70;
/** حداقل تعداد ماه دادهٔ تاریخی برای اعتماد کامل (فاصله خرید/گروه‌بندی). */
export const DEFAULT_MONTHS_OF_DATA_THRESHOLD = 6;

const fmtNumber = (value: number): string => Math.round(value).toLocaleString('fa-IR');

export function computeInvoiceCountConfidence(
  invoiceCount: number,
  threshold: number = DEFAULT_INVOICE_COUNT_THRESHOLD,
): ConfidenceResult {
  const safeCount = Number.isFinite(invoiceCount) ? Math.max(0, invoiceCount) : 0;
  const level: ConfidenceLevel = safeCount >= threshold ? 'good' : 'warning';
  const formatted = fmtNumber(safeCount);

  return {
    level,
    // برچسب: مجموع کل فاکتورهای ثبت‌شده بازار (BI_BranchDetails)
    label: `شاخص اعتماد · فاکتورهای بررسی‌شده بازار: ${formatted}`,
    detail:
      'شاخص اعتماد (تعداد فاکتورهای تحلیل‌شده):این عدد نشان‌دهنده‌ی حجم داده‌های بازار است که گزارش حاضر بر مبنای آن‌ها استخراج شده است'
  };
}

export function computeSubscriptionConfidence(
  subscriptionPercent: number,
  threshold: number = DEFAULT_SUBSCRIPTION_THRESHOLD_PERCENT,
): ConfidenceResult {
  const safePercent = Number.isFinite(subscriptionPercent)
    ? Math.max(0, Math.min(100, subscriptionPercent))
    : 0;
  const level: ConfidenceLevel = safePercent >= threshold ? 'good' : 'warning';
  const formatted = fmtNumber(safePercent);

  return {
    level,
    label: `اعتماد · ${formatted}٪ اشتراک`,
    detail:
      level === 'good'
        ? `${formatted}٪ سفارش‌های این بازه اشتراک ثبت‌شده دارند؛ داده برای این تحلیل قابل اتکا است.`
        : `فقط ${formatted}٪ سفارش‌های این بازه اشتراک ثبت‌شده دارند (آستانهٔ قابل‌اتکا: ${fmtNumber(threshold)}٪)؛ نتایج ممکن است کامل نباشد.`,
  };
}

export function computeMonthsOfDataConfidence(
  monthsOfData: number,
  threshold: number = DEFAULT_MONTHS_OF_DATA_THRESHOLD,
): ConfidenceResult {
  const safeMonths = Number.isFinite(monthsOfData) ? Math.max(0, Math.floor(monthsOfData)) : 0;
  const level: ConfidenceLevel = safeMonths >= threshold ? 'good' : 'warning';
  const formatted = fmtNumber(safeMonths);

  return {
    level,
    label: level === 'good' ? `اعتماد · ${formatted}+ ماه` : `اعتماد · ${formatted} ماه`,
    detail:
      level === 'good'
        ? `این تحلیل بر اساس ${formatted} ماه دادهٔ تاریخی محاسبه شده و قابل اتکا است.`
        : `فقط ${formatted} ماه دادهٔ تاریخی موجود است (حداقل ${fmtNumber(threshold)} ماه برای اعتماد کامل توصیه می‌شود)؛ نتایج ممکن است نوسان بیشتری داشته باشد.`,
  };
}
